import { z } from 'zod'
import { durationMinutes, localDayAt, parseIsoInstant } from './time'
import type {
  ManualMetricInput,
  ManualSleepInput,
  ManualWorkoutInput,
  MetricRecord,
  SleepRecord,
  WorkoutRecord,
} from './types'
import { lbToKg } from './units'

const MAX_WEIGHT_KG = 500
const MAX_BODY_FAT_PERCENT = 100
const MAX_ACTIVE_ENERGY_KCAL = 20_000
const MIN_DURATION_MINUTES = 1
const MAX_DURATION_MINUTES = 24 * 60
const MAX_FUTURE_MS = 5 * 60 * 1000
const MAX_NOTES_CODE_POINTS = 1_000

const FUTURE_ERROR = '记录时间不能晚于当前时间 5 分钟以上'
const ENERGY_MIN_ERROR = '活动卡路里不能小于 0 kcal'
const ENERGY_MAX_ERROR = '活动卡路里不能超过 20,000 kcal'

const finiteNumberSchema = z.number().refine(Number.isFinite)
const timeZoneSchema = z.string().min(1)
const isoInstantSchema = z.string().refine((value) => {
  try {
    parseIsoInstant(value)
    return true
  } catch {
    return false
  }
})

const metricInputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('weight'),
    value: finiteNumberSchema,
    inputUnit: z.enum(['kg', 'lb']),
    recordedAt: isoInstantSchema,
    timeZone: timeZoneSchema,
  }),
  z.object({
    type: z.literal('bodyFat'),
    value: finiteNumberSchema,
    recordedAt: isoInstantSchema,
    timeZone: timeZoneSchema,
  }),
  z.object({
    type: z.literal('activeEnergy'),
    value: finiteNumberSchema,
    recordedAt: isoInstantSchema,
    timeZone: timeZoneSchema,
  }),
])

const sleepInputSchema = z.object({
  startAt: isoInstantSchema,
  endAt: isoInstantSchema,
  wakeTimeZone: timeZoneSchema,
})

const workoutInputSchema = z.object({
  title: z.string().trim().refine((value) => [...value].length <= 40).optional(),
  status: z.literal('planned').optional(),
  activityType: z.enum([
    'strength',
    'running',
    'walking',
    'cycling',
    'swimming',
    'yoga',
    'other',
  ]),
  startedAt: isoInstantSchema,
  timeZone: timeZoneSchema,
  durationMinutes: finiteNumberSchema,
  activeEnergyKcal: finiteNumberSchema.optional(),
  notes: z.string().optional(),
})

function parseShape<T>(schema: z.ZodType<T>, input: unknown): T {
  const result = schema.safeParse(input)
  if (!result.success) {
    const field = result.error.issues[0]?.path.join('.')
    throw new Error(field ? `输入格式无效：${field}` : '输入格式无效')
  }
  return result.data
}

function normalizeInstant(iso: string): string {
  return parseIsoInstant(iso).toISOString()
}

function assertNotTooFarInFuture(iso: string, now: Date): void {
  if (parseIsoInstant(iso).getTime() > now.getTime() + MAX_FUTURE_MS) {
    throw new Error(FUTURE_ERROR)
  }
}

function manualTimestamps(now: Date): Pick<MetricRecord, 'createdAt' | 'updatedAt'> {
  const timestamp = now.toISOString()
  return { createdAt: timestamp, updatedAt: timestamp }
}

export function parseMetricInput(input: ManualMetricInput, now: Date): MetricRecord {
  const parsed = parseShape(metricInputSchema, input)
  const recordedAt = normalizeInstant(parsed.recordedAt)
  assertNotTooFarInFuture(recordedAt, now)

  let value: number
  let unit: MetricRecord['unit']
  if (parsed.type === 'weight') {
    value = parsed.inputUnit === 'lb' ? lbToKg(parsed.value) : parsed.value
    unit = 'kg'
    if (value <= 0) throw new Error('体重必须大于 0 kg')
    if (value > MAX_WEIGHT_KG) throw new Error('体重不能超过 500 kg')
  } else if (parsed.type === 'bodyFat') {
    value = parsed.value
    unit = 'percent'
    if (value <= 0) throw new Error('体脂率必须大于 0%')
    if (value > MAX_BODY_FAT_PERCENT) throw new Error('体脂率不能超过 100%')
  } else {
    value = parsed.value
    unit = 'kcal'
    if (value < 0) throw new Error(ENERGY_MIN_ERROR)
    if (value > MAX_ACTIVE_ENERGY_KCAL) throw new Error(ENERGY_MAX_ERROR)
  }

  return {
    id: crypto.randomUUID(),
    type: parsed.type,
    value,
    unit,
    recordedAt,
    timeZone: parsed.timeZone,
    localDay: localDayAt(recordedAt, parsed.timeZone),
    source: 'manual',
    ...manualTimestamps(now),
  }
}

export function parseSleepInput(
  input: ManualSleepInput,
  existing: readonly SleepRecord[],
  now: Date,
  editingId?: string,
): SleepRecord {
  const parsed = parseShape(sleepInputSchema, input)
  const startAt = normalizeInstant(parsed.startAt)
  const endAt = normalizeInstant(parsed.endAt)
  const startMs = parseIsoInstant(startAt).getTime()
  const endMs = parseIsoInstant(endAt).getTime()

  if (endMs <= startMs) throw new Error('睡眠结束时间必须晚于开始时间')
  const minutes = durationMinutes(startAt, endAt)
  if (minutes < MIN_DURATION_MINUTES) throw new Error('睡眠时长不能少于 1 分钟')
  if (minutes > MAX_DURATION_MINUTES) throw new Error('睡眠时长不能超过 24 小时')
  assertNotTooFarInFuture(endAt, now)

  const wakeLocalDay = localDayAt(endAt, parsed.wakeTimeZone)
  const otherRecords = existing.filter((record) => record.id !== editingId)
  if (
    otherRecords.some(
      (record) =>
        startMs < parseIsoInstant(record.endAt).getTime() &&
        endMs > parseIsoInstant(record.startAt).getTime(),
    )
  ) {
    throw new Error('睡眠时间不能与现有记录重叠')
  }
  if (otherRecords.some((record) => record.wakeLocalDay === wakeLocalDay)) {
    throw new Error('同一唤醒日只能有一条睡眠记录')
  }

  return {
    id: crypto.randomUUID(),
    startAt,
    endAt,
    durationMinutes: minutes,
    wakeTimeZone: parsed.wakeTimeZone,
    wakeLocalDay,
    source: 'manual',
    ...manualTimestamps(now),
  }
}

export function parseWorkoutInput(input: ManualWorkoutInput, now: Date): WorkoutRecord {
  const parsed = parseShape(workoutInputSchema, input)
  if (parsed.durationMinutes < MIN_DURATION_MINUTES) {
    throw new Error('训练时长不能少于 1 分钟')
  }
  if (parsed.durationMinutes > MAX_DURATION_MINUTES) {
    throw new Error('训练时长不能超过 24 小时')
  }
  if (parsed.activeEnergyKcal !== undefined) {
    if (parsed.activeEnergyKcal < 0) throw new Error(ENERGY_MIN_ERROR)
    if (parsed.activeEnergyKcal > MAX_ACTIVE_ENERGY_KCAL) throw new Error(ENERGY_MAX_ERROR)
  }
  if (parsed.notes !== undefined && [...parsed.notes].length > MAX_NOTES_CODE_POINTS) {
    throw new Error('备注不能超过 1,000 个 Unicode 字符')
  }

  const startedAt = normalizeInstant(parsed.startedAt)
  const endAt = new Date(
    parseIsoInstant(startedAt).getTime() + parsed.durationMinutes * 60_000,
  ).toISOString()
  if (parsed.status !== 'planned') assertNotTooFarInFuture(endAt, now)

  return {
    id: crypto.randomUUID(),
    activityType: parsed.activityType,
    ...(parsed.title ? { title: parsed.title } : {}),
    ...(parsed.status === 'planned' ? { status: parsed.status } : {}),
    startedAt,
    timeZone: parsed.timeZone,
    localDay: localDayAt(startedAt, parsed.timeZone),
    durationMinutes: parsed.durationMinutes,
    ...(parsed.activeEnergyKcal === undefined
      ? {}
      : { activeEnergyKcal: parsed.activeEnergyKcal }),
    ...(parsed.notes === undefined ? {} : { notes: parsed.notes }),
    source: 'manual',
    ...manualTimestamps(now),
  }
}
